/* 首页横幅：图片兜底、视频首帧切换、文字轮播及品牌滚动分别管理各自生命周期。 */
const BANNER_TIMING = Object.freeze({ videoTimeout: 15000, videoDownloadTimeout: 120000, dependencyRetry: 120, dependencyAttempts: 80 });

/** 校验后台或数据属性中的数值，避免非法值产生计时器风暴。 */
function boundedNumber(value, fallback, min, max) {
  const number = Number(value);
  return Number.isFinite(number) && number >= min && number <= max ? number : fallback;
}

/**
 * 输入：包含封面和 video 的媒体容器；输出：激活、暂停、销毁三个生命周期动作。
 * 完整下载 MP4 后才允许播放，并在真实首帧出现后揭开封面；失败时保留图片。
 * 外部视频须允许跨域读取。离开视口保留下载，切换设备或销毁时取消请求并释放资源。
 */
export function createVideoController(media) {
  const video = media.querySelector('video');
  const poster = media.querySelector('.index-banner-image');
  const listeners = new AbortController();
  const mobileViewport = globalThis.matchMedia?.('(max-width: 767px)');
  let active = false;
  let destroyed = false;
  let loaded = false;
  let pending = false;
  let failed = false;
  let generation = 0;
  let frameId = null;
  let timeoutId = null;
  let downloadController = null;
  let videoObjectUrl = null;

  /** 下载整个响应体；不使用 canplaythrough 预测，也不退回边下载边播放。 */
  async function downloadVideo(address) {
    const parsed = new URL(address, globalThis.location?.href || 'https://localhost/');
    if (!['https:', 'http:'].includes(parsed.protocol)) throw new Error('视频地址协议无效');
    const request = new AbortController();
    downloadController = request;
    const downloadDeadline = setTimeout(() => request.abort(), BANNER_TIMING.videoDownloadTimeout);
    try {
      const response = await fetch(parsed.href, { signal: request.signal, credentials: 'omit' });
      // 部分响应不能作为完整文件播放，HTTP 错误页和空文件也必须保留封面。
      if (!response.ok || response.status === 206) throw new Error(`视频完整下载失败（HTTP ${response.status}）`);
      const blob = await response.blob();
      if (!blob.size || (blob.type && !/^(video\/|application\/octet-stream(?:;|$))/i.test(blob.type))) {
        throw new Error('视频文件为空或响应格式无效');
      }
      if (destroyed || request.signal.aborted || downloadController !== request) return;
      videoObjectUrl = URL.createObjectURL(blob);
      video.src = videoObjectUrl;
      video.muted = true;
      video.load();
      loaded = true;
    } finally {
      clearTimeout(downloadDeadline);
      if (downloadController === request) downloadController = null;
    }
  }

  /** 取消旧设备的下载并释放本地视频，避免切换或卸载后继续占用内存。 */
  function releaseVideo() {
    downloadController?.abort();
    downloadController = null;
    loaded = false;
    video.removeAttribute('src');
    video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
    video.load();
    if (videoObjectUrl) URL.revokeObjectURL(videoObjectUrl);
    videoObjectUrl = null;
  }

  function clearPendingFrame() {
    clearTimeout(timeoutId);
    timeoutId = null;
    if (frameId !== null && video.cancelVideoFrameCallback) video.cancelVideoFrameCallback(frameId);
    frameId = null;
  }

  function restoreImage(reason) {
    clearPendingFrame();
    media.classList.remove('is-video-visible');
    video.pause();
    if (reason) {
      failed = true;
      console.warn('[Index Banner Slider] 视频暂不可用，继续展示封面。', reason);
    }
  }

  function revealFrame() {
    if (!active || destroyed || failed || !loaded || video.paused) return;
    clearPendingFrame();
    media.classList.add('is-video-visible');
  }

  video.addEventListener('playing', () => {
    if (!active || destroyed || failed) return;
    const currentGeneration = generation;
    // 新浏览器直接等待合成的视频帧；旧浏览器以已前进的播放时间作为兼容判断。
    if (video.requestVideoFrameCallback) {
      frameId = video.requestVideoFrameCallback(() => {
        if (generation === currentGeneration) revealFrame();
      });
    }
  }, { signal: listeners.signal });
  video.addEventListener('timeupdate', () => {
    if (!video.requestVideoFrameCallback && video.readyState >= 2 && video.currentTime > 0) revealFrame();
  }, { signal: listeners.signal });
  video.addEventListener('error', () => {
    if (active && loaded) restoreImage('媒体加载错误');
  }, { signal: listeners.signal });

  async function activate() {
    if (destroyed || failed || active) return;
    active = true;
    const currentGeneration = ++generation;
    // 电脑与手机各用自己的视频；当前设备未配置视频时保留封面，不借用另一端媒体。
    const sources = [...video.querySelectorAll('source')];
    const addresses = sources.map(source => mobileViewport?.matches ? source.dataset.mobileSrc : source.dataset.src);
    if (!addresses.length || addresses.some(address => !address)) return;
    if (pending) return;
    pending = true;
    try {
      // 封面尚未解码时不抢占首屏图片带宽。失败后仍允许视频提供有效画面。
      if (poster?.decode) await poster.decode().catch(() => {});
      if (!active || destroyed || generation !== currentGeneration) return;
      // 慢网提示不提前开播；超过下载上限则取消请求并保留封面。
      timeoutId = setTimeout(() => {
        timeoutId = null;
        console.warn('[Index Banner Slider] 视频仍在完整下载，暂时显示封面，下载完成后自动播放。');
      }, BANNER_TIMING.videoTimeout);
      // snippet 为当前设备提供一个 MP4 地址；完整文件仅下载一次，循环和重入复用本地资源。
      if (!loaded) await downloadVideo(addresses[0]);
      if (!loaded || !active || destroyed || generation !== currentGeneration) return;
      await video.play();
    } catch (error) {
      // 退出视口造成的 AbortError 不算永久失败，回到此幻灯片时可以继续。
      if (active && generation === currentGeneration) restoreImage(error.message || error.name || '自动播放被阻止');
    } finally {
      pending = false;
      if (!destroyed && active && generation !== currentGeneration) {
        active = false;
        void activate();
      }
    }
  }

  function deactivate() {
    active = false;
    generation++;
    restoreImage();
  }

  function destroy() {
    destroyed = true;
    deactivate();
    listeners.abort();
    releaseVideo();
  }
  // 编辑器切换设备宽度时不会总是重建区块，需要主动取消旧请求并切换对应媒体。
  mobileViewport?.addEventListener('change', () => {
    const shouldResume = active;
    deactivate();
    releaseVideo();
    failed = false;
    if (shouldResume) void activate();
  }, { signal: listeners.signal });
  return { activate, deactivate, destroy };
}

/**
 * 文案按 DOM 顺序逐项淡入并停留：产品名、标题各行、说明、按钮。
 * 动画只播放一次；离开视口时暂停，减少动态效果时立即显示所有文案。
 */
export function createCopyRevealController(slide, host) {
  if (!slide.querySelector('.index-banner-title-list')) return { start() {}, stop() {}, show() {}, destroy() {} };
  const items = [...slide.querySelectorAll('.index-banner-topline, .index-banner-title-line, .index-banner-description-layout, .index-banner-footer .index-banner-button-wrap')];
  const duration = boundedNumber(host.dataset.titleSpeed, 500, 200, 1500);
  const delay = boundedNumber(host.dataset.titleDelay, 750, 250, 8000);
  let destroyed = false;
  slide.style.setProperty('--index-banner-copy-duration', `${duration}ms`);
  items.forEach((item, index) => {
    item.dataset.copyReveal = '';
    item.style.setProperty('--index-banner-copy-delay', `${index * delay}ms`);
  });
  // 只有控制器成功初始化才隐藏等待淡入的文字，无脚本时仍可阅读全部内容。
  slide.classList.add('has-copy-reveal');
  return {
    start() { if (!destroyed) slide.classList.add('is-copy-revealing'); },
    stop() { slide.classList.remove('is-copy-revealing'); },
    show() { if (!destroyed) slide.classList.add('is-copy-complete'); },
    destroy() {
      destroyed = true;
      slide.classList.remove('has-copy-reveal', 'is-copy-revealing', 'is-copy-complete');
      slide.style.removeProperty('--index-banner-copy-duration');
      items.forEach(item => {
        delete item.dataset.copyReveal;
        item.style.removeProperty('--index-banner-copy-delay');
      });
    },
  };
}

/** 品牌保持单一真实列表；复制项仅用于视觉衔接，不能被键盘或辅助技术重复访问。 */
function createBrandController(host) {
  const region = host.querySelector('.index-banner-brands');
  if (!region) return { start() {}, stop() {}, destroy() {} };
  const viewport = region.querySelector('.index-banner-brand-viewport');
  const track = region.querySelector('.index-banner-brand-track');
  const group = track.firstElementChild;
  const listeners = new AbortController();
  const speed = boundedNumber(host.dataset.brandSpeed, 25, 10, 100);
  const direction = host.dataset.brandDirection === 'right' ? -1 : 1;
  let animation = null;
  let previousTime = 0;
  let enabled = false;
  let dragging = false;
  let period = 0;
  let position = 0;

  function rebuild() {
    track.querySelectorAll('[data-brand-copy]').forEach(copy => copy.remove());
    period = group.getBoundingClientRect().width;
    if (!period || !viewport.clientWidth) return;
    const repeat = Math.min(30, Math.ceil(viewport.clientWidth / period) + 1);
    for (let index = 0; index < repeat; index++) {
      const copy = group.cloneNode(true);
      copy.dataset.brandCopy = '';
      copy.setAttribute('aria-hidden', 'true');
      // 副本仍可鼠标打开原链接，但不进入键盘焦点序列和辅助技术阅读顺序。
      copy.querySelectorAll('a').forEach(link => { link.tabIndex = -1; });
      copy.querySelectorAll('[data-shopify-editor-block]').forEach(item => item.removeAttribute('data-shopify-editor-block'));
      track.append(copy);
    }
    viewport.scrollLeft = direction < 0 ? period : 0;
    position = viewport.scrollLeft;
  }

  function step(time) {
    if (!enabled) return;
    const elapsed = previousTime ? Math.min(time - previousTime, 64) / 1000 : 0;
    previousTime = time;
    if (!dragging && !region.matches(':hover, :focus-within') && period > 0) {
      // 保留亚像素累计值，避免低速滚动每帧被 scrollLeft 的整数舍入吞掉。
      position = (position + direction * speed * elapsed + period) % period;
      viewport.scrollLeft = position;
    } else position = viewport.scrollLeft;
    animation = requestAnimationFrame(step);
  }

  function stop() { enabled = false; cancelAnimationFrame(animation); animation = null; previousTime = 0; position = viewport.scrollLeft; }
  const observeOptions = { signal: listeners.signal };
  viewport.addEventListener('pointerdown', () => { dragging = true; }, observeOptions);
  globalThis.addEventListener('pointerup', () => { dragging = false; }, observeOptions);
  globalThis.addEventListener('pointercancel', () => { dragging = false; }, observeOptions);
  viewport.addEventListener('keydown', event => {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    // 去掉视觉箭头后保留方向键浏览，同时继续支持触摸与滚轮横向滚动。
    if (event.key === 'ArrowLeft' && viewport.scrollLeft < 1) viewport.scrollLeft = period;
    viewport.scrollBy({ left: (event.key === 'ArrowLeft' ? -1 : 1) * viewport.clientWidth * 0.6, behavior: 'auto' });
    if (viewport.scrollLeft >= period) viewport.scrollLeft -= period;
  }, observeOptions);
  const observer = globalThis.ResizeObserver ? new ResizeObserver(rebuild) : null;
  observer?.observe(viewport);
  rebuild();
  return {
    start() { if (!enabled) { enabled = true; position = viewport.scrollLeft; animation = requestAnimationFrame(step); } },
    stop,
    destroy() { stop(); observer?.disconnect(); listeners.abort(); track.querySelectorAll('[data-brand-copy]').forEach(copy => copy.remove()); },
  };
}

class IndexBannerSlider extends (globalThis.HTMLElement || class {}) {
  connectedCallback() {
    if (this.cleanup) return;
    const listeners = new AbortController();
    const options = { signal: listeners.signal };
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const slides = [...this.querySelectorAll('.index-banner-slide')];
    const videoControllers = new Map(slides.map(slide => [slide, slide.querySelector('video') ? createVideoController(slide.querySelector('.index-banner-slide-media-wrap')) : null]));
    const copyControllers = new Map(slides.map(slide => [slide, createCopyRevealController(slide, this)]));
    const brands = createBrandController(this);
    let swiper = null;
    let visible = false;
    let editorSelection = false;
    let destroyed = false;
    let retries = 0;
    let retryTimer = null;

    const sync = () => {
      // 编辑器移除区块后，已排队的焦点、切换回调不能访问已销毁的轮播实例。
      if (destroyed || !this.isConnected) return;
      const inView = visible && !document.hidden;
      const allowMotion = inView && !reducedMotion.matches;
      const allowAutoplay = allowMotion && !editorSelection && !this.matches(':hover, :focus-within');
      const currentSlide = slides[swiper?.realIndex || 0];
      for (const slide of slides) {
        const current = slide === currentSlide;
        slide.inert = !current;
        slide.setAttribute('aria-hidden', String(!current));
        if (current && allowMotion) void videoControllers.get(slide)?.activate();
        else videoControllers.get(slide)?.deactivate();
        // 入场淡入不随鼠标悬停反复启动，视频循环也不会重置已经出现的文案。
        const copy = copyControllers.get(slide);
        if (reducedMotion.matches) copy.show();
        if (current && inView && !reducedMotion.matches) copy.start();
        else copy.stop();
      }
      if (swiper) {
        swiper.params.speed = reducedMotion.matches ? 0 : boundedNumber(this.dataset.speed, 850, 200, 2000);
        if (allowAutoplay && this.dataset.autoplay === 'true') swiper.autoplay?.start();
        else swiper.autoplay?.stop();
      }
      // 品牌是独立滚动区域，不随整个 Banner 的悬停或幻灯片编辑选中而暂停。
      // 区域内部的悬停、键盘焦点和拖动仍由品牌控制器负责暂停。
      if (allowMotion) brands.start(); else brands.stop();
    };

    const initSwiper = () => {
      if (!this.isConnected) return;
      if (slides.length < 2) { sync(); return; }
      if (typeof globalThis.Swiper !== 'function') {
        if (++retries < BANNER_TIMING.dependencyAttempts) retryTimer = setTimeout(initSwiper, BANNER_TIMING.dependencyRetry);
        else console.warn('[Index Banner Slider] 轮播依赖未加载，保留首屏图片与文字。');
        return;
      }
      swiper = new globalThis.Swiper(this.querySelector('.index-banner-slider-swiper'), {
        wrapperClass: 'index-swiper-wrapper', slideClass: 'index-swiper-slide',
        slideActiveClass: 'index-swiper-slide-active', slideVisibleClass: 'index-swiper-slide-visible',
        slideDuplicateClass: 'index-swiper-slide-duplicate', slideDuplicateActiveClass: 'index-swiper-slide-duplicate-active',
        loop: this.dataset.loop === 'true',
        speed: reducedMotion.matches ? 0 : boundedNumber(this.dataset.speed, 850, 200, 2000),
        effect: this.dataset.effect === 'slide' ? 'slide' : 'fade', fadeEffect: { crossFade: true },
        autoplay: { enabled: false, delay: boundedNumber(this.dataset.delay, 3500, 1000, 9000), disableOnInteraction: false, pauseOnMouseEnter: true },
        navigation: { nextEl: this.querySelector('.index-banner-nav-next'), prevEl: this.querySelector('.index-banner-nav-prev') },
        pagination: { el: this.querySelector('.index-banner-pagination'), clickable: true, bulletClass: 'index-swiper-pagination-bullet', bulletActiveClass: 'index-swiper-pagination-bullet-active' },
        on: { slideChange: () => queueMicrotask(sync) },
      });
      sync();
    };

    const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); }, { threshold: 0.01 });
    observer.observe(this);
    ['mouseenter', 'mouseleave', 'focusin'].forEach(event => this.addEventListener(event, sync, options));
    this.addEventListener('focusout', () => queueMicrotask(sync), options);
    document.addEventListener('visibilitychange', sync, options);
    reducedMotion.addEventListener('change', sync, options);
    this.addEventListener('shopify:block:select', event => {
      editorSelection = true;
      const position = slides.findIndex(slide => slide.dataset.slideId === event.detail?.blockId);
      if (position >= 0) {
        if (swiper?.params.loop) swiper.slideToLoop(position, 0); else swiper?.slideTo(position, 0);
      } else event.target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      sync();
    }, options);
    this.addEventListener('shopify:block:deselect', () => { editorSelection = false; sync(); }, options);
    this.cleanup = () => {
      destroyed = true;
      clearTimeout(retryTimer); listeners.abort(); observer.disconnect();
      videoControllers.forEach(controller => controller?.destroy());
      copyControllers.forEach(controller => controller.destroy());
      brands.destroy(); swiper?.destroy(true, true);
    };
    initSwiper();
  }

  disconnectedCallback() { this.cleanup?.(); this.cleanup = null; }
}

if (globalThis.customElements && !customElements.get('index-banner-slider')) customElements.define('index-banner-slider', IndexBannerSlider);
